import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  useBranding,
  useUpdateBranding,
  useUploadBrandingLogo,
  useDeleteBrandingLogo,
} from '@/hooks/useApi';
import PageSkeleton from '@/components/PageSkeleton';
import type { InvoiceTemplate } from '@/types';

const FONT_FAMILIES = [
  'Inter',
  'Helvetica',
  'Arial',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Trebuchet MS',
];

const LOGO_POSITIONS = ['left', 'center', 'right'] as const;

export default function BrandingSettingsPage() {
  const { t } = useTranslation();
  const { data: template, isLoading } = useBranding();
  const updateBranding = useUpdateBranding();
  const uploadLogo = useUploadBrandingLogo();
  const deleteLogo = useDeleteBrandingLogo();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [colors, setColors] = useState<InvoiceTemplate['colors']>(
    template?.colors || {
      primary: '#4F46E5',
      secondary: '#6B7280',
      text: '#111827',
      background: '#FFFFFF',
    }
  );

  const [layout, setLayout] = useState<InvoiceTemplate['layout']>(
    template?.layout || {
      showLogo: true,
      logoPosition: 'left',
      showBankDetails: true,
      footerText: '',
    }
  );

  const [fontFamily, setFontFamily] = useState(template?.fontFamily || 'Inter');

  // Sync form state with fetched template data
  useEffect(() => {
    if (template) {
      setColors(template.colors || {
        primary: '#4F46E5',
        secondary: '#6B7280',
        text: '#111827',
        background: '#FFFFFF',
      });
      setLayout(template.layout || {
        showLogo: true,
        logoPosition: 'left',
        showBankDetails: true,
        footerText: '',
      });
      setFontFamily(template.fontFamily || 'Inter');
    }
  }, [template]);

  if (isLoading) return <PageSkeleton variant="detail" />;

  const handleColorChange = (key: keyof typeof colors, value: string) => {
    setColors({ ...colors, [key]: value });
  };

  const handleLayoutChange = (key: string, value: any) => {
    setLayout({ ...layout, [key]: value });
  };

  const handleSave = async () => {
    try {
      await updateBranding.mutateAsync({ colors, layout, fontFamily });
    } catch {
      // Error handled by hook
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.match(/image\/(png|jpeg|svg\+xml)/)) {
      toast.error(t('branding.invalidFileType'));
      return;
    }

    if (file.size > 1048576) {
      toast.error(t('branding.fileTooLarge'));
      return;
    }

    try {
      await uploadLogo.mutateAsync(file);
    } catch {
      // Error handled by hook
    }
  };

  const handleRemoveLogo = async () => {
    if (window.confirm(t('branding.confirmRemoveLogo'))) {
      try {
        await deleteLogo.mutateAsync();
      } catch {
        // Error handled by hook
      }
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('branding.title')}</h1>
        <p className="text-gray-600 mt-1">{t('branding.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-20">
        {/* Preview Section - Non-sticky */}
        <div className="lg:col-span-1">
          <div className="card card-body space-y-4">
            <h3 className="text-lg font-semibold">{t('branding.preview')}</h3>

            {/* Logo Preview */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300 aspect-video flex items-center justify-center">
              {template?.logo ? (
                <img src={template.logo} alt="Logo preview" className="max-h-20 max-w-full" />
              ) : (
                <div className="text-center">
                  <PhotoIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t('branding.noLogo')}</p>
                </div>
              )}
            </div>

            {/* Color Preview */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">{t('branding.colorPreview')}</p>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div
                    className="w-16 h-16 rounded-lg border"
                    style={{ backgroundColor: colors.primary }}
                  />
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-gray-600">{t('branding.primary')}</p>
                    <p className="text-sm font-mono">{colors.primary}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div
                    className="w-16 h-16 rounded-lg border"
                    style={{ backgroundColor: colors.secondary }}
                  />
                  <div className="flex flex-col justify-center">
                    <p className="text-xs text-gray-600">{t('branding.secondary')}</p>
                    <p className="text-sm font-mono">{colors.secondary}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Font Preview */}
            <div className="pt-2">
              <p className="text-sm font-medium text-gray-700 mb-2">{t('branding.fontPreview')}</p>
              <p style={{ fontFamily, color: colors.text }}>{fontFamily}</p>
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Logo Upload */}
          <div className="card card-body space-y-4">
            <h3 className="text-lg font-semibold">{t('branding.logoSection')}</h3>

            {template?.logo && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="text-sm font-medium text-blue-900">{t('branding.logoPresent')}</p>
                  <p className="text-xs text-blue-700">{template.logoPath}</p>
                </div>
                <button
                  onClick={handleRemoveLogo}
                  disabled={deleteLogo.isPending}
                  className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('branding.uploadLogo')}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={onFileInputChange}
                disabled={uploadLogo.isPending}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLogo.isPending}
                className="btn btn-outline w-full"
              >
                {uploadLogo.isPending ? t('common.uploading') : t('branding.chooseFile')}
              </button>
              <p className="text-xs text-gray-500 mt-2">{t('branding.logoHint')}</p>
            </div>
          </div>

          {/* Colors */}
          <div className="card card-body space-y-4">
            <h3 className="text-lg font-semibold">{t('branding.colorsSection')}</h3>

            <div className="grid grid-cols-2 gap-4">
              {(Object.entries(colors) as Array<[keyof typeof colors, string]>).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {t(`branding.${key}`)}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="input flex-1"
                      placeholder="#000000"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Layout */}
          <div className="card card-body space-y-4">
            <h3 className="text-lg font-semibold">{t('branding.layoutSection')}</h3>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={layout.showLogo}
                  onChange={(e) => handleLayoutChange('showLogo', e.target.checked)}
                  className="checkbox"
                />
                <span className="text-sm font-medium">{t('branding.showLogo')}</span>
              </label>
            </div>

            {layout.showLogo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('branding.logoPosition')}
                </label>
                <div className="flex gap-2">
                  {LOGO_POSITIONS.map((pos) => (
                    <button
                      key={pos}
                      onClick={() => handleLayoutChange('logoPosition', pos)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        layout.logoPosition === pos
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t(`branding.position${pos.charAt(0).toUpperCase() + pos.slice(1)}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={layout.showBankDetails}
                  onChange={(e) => handleLayoutChange('showBankDetails', e.target.checked)}
                  className="checkbox"
                />
                <span className="text-sm font-medium">{t('branding.showBankDetails')}</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('branding.footerText')}
              </label>
              <textarea
                value={layout.footerText}
                onChange={(e) => handleLayoutChange('footerText', e.target.value)}
                placeholder={t('branding.footerPlaceholder')}
                className="input h-20 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{t('branding.footerHint')}</p>
            </div>
          </div>

          {/* Font */}
          <div className="card card-body space-y-4">
            <h3 className="text-lg font-semibold">{t('branding.fontSection')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('branding.fontFamily')}
              </label>
              <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="input"
              >
                {FONT_FAMILIES.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleSave}
              disabled={updateBranding.isPending}
              className="btn btn-primary"
            >
              {updateBranding.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
